import { useEffect, useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTypography from "../../system-design/DSTypography";
import DSBox from "../../system-design/DSBox";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import { CrashBundle } from "../../../api/crashBundles";
import useAnalysisModelsQuery from "../hooks/useAnalysisModelsQuery";
import { useRunAnalysisMutation } from "../hooks/useCrashBundleMutations";

type Props = {
  show: boolean;
  bundle: CrashBundle | null;
  onClose: () => void;
};

function RunAnalysisModal({ show, bundle, onClose }: Props) {
  const { data: models, isLoading: modelsLoading } =
    useAnalysisModelsQuery(show);
  const [model, setModel] = useState("");
  const runAnalysis = useRunAnalysisMutation();

  useEffect(() => {
    if (models?.length && !model) setModel(models[0].id);
  }, [models, model]);

  // Reset transient state whenever a new bundle is opened.
  useEffect(() => {
    if (show) runAnalysis.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, bundle?.fileKey]);

  const handleRun = () => {
    if (bundle && model) runAnalysis.mutate({ fileKey: bundle.fileKey, model });
  };

  return (
    <DSDialog open={show} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>Run crash analysis</DSDialogTitle>
      <DSDialogContent>
        {runAnalysis.isSuccess ? (
          <DSTypography>
            Running analysis… the diagnosis will be posted to the engineers
            Slack channel shortly.
          </DSTypography>
        ) : runAnalysis.isPending ? (
          <DSBox sx={{ textAlign: "center", p: "24px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : (
          <>
            <DSTypography sx={{ mb: 2 }}>
              Bundle: <b>{bundle?.fileName}</b>
            </DSTypography>
            {modelsLoading ? (
              <DSCircularProgress />
            ) : models && models.length > 0 ? (
              <DSSelect
                fullWidth
                value={model}
                onChange={(e: SelectChangeEvent<unknown>) =>
                  setModel(e.target.value as string)
                }
              >
                {models.map((m) => (
                  <DSMenuItem key={m.id} value={m.id}>
                    {m.label} ({m.provider})
                  </DSMenuItem>
                ))}
              </DSSelect>
            ) : (
              <DSTypography>
                No analysis models are configured on the API.
              </DSTypography>
            )}
            {runAnalysis.isError && (
              <DSTypography color="error" sx={{ mt: 2 }}>
                Failed to start analysis. Please try again.
              </DSTypography>
            )}
          </>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>
          {runAnalysis.isSuccess ? "Close" : "Cancel"}
        </DSSecondaryButton>
        {!runAnalysis.isSuccess && (
          <DSPrimaryButton
            onClick={handleRun}
            disabled={!model || runAnalysis.isPending}
          >
            Run analysis
          </DSPrimaryButton>
        )}
      </DSDialogActions>
    </DSDialog>
  );
}

export default RunAnalysisModal;
